import { Button } from '@seen/ui'
import { useCallback, useEffect, useState } from 'react'
import semver from 'semver'
import { toast } from 'sonner'
import { useQuery } from '#src/hooks/useQuery.ts'

interface VersionResponse {
  version: string
  commitSha: string
  buildTime: string
}

interface VersionCheckProps {
  appVersion?: string
  appCommitSha?: string
  checkInterval?: number
}

export function useVersionCheck({
  appVersion: localAppVersion,
  checkInterval = 60 * 1000,
}: VersionCheckProps): void {
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const [hasShownToast, setHasShownToast] = useState(false)

  const { data: remoteVersionData } = useQuery<VersionResponse>({
    queryKey: ['version'],
    queryFn: async () => {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      })
      if (!response.ok) {
        throw new Error('Failed to fetch version')
      }
      return response.json()
    },
    refetchInterval: checkInterval,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 3,
    enabled: localAppVersion !== 'development',
  })

  const refreshApp = useCallback((): void => {
    window.location.reload()
  }, [])

  useEffect(() => {
    if (!remoteVersionData || needsRefresh) {
      return
    }

    const remoteVersion = remoteVersionData.version
    if (localAppVersion && remoteVersion) {
      const localValid = semver.valid(localAppVersion)
      const remoteValid = semver.valid(remoteVersion)

      if (localValid && remoteValid) {
        const majorDiff = semver.diff(localAppVersion, remoteVersion)

        if (majorDiff === 'major') {
          setNeedsRefresh(true)

          if (!hasShownToast) {
            setHasShownToast(true)
            toast.info('New version available', {
              id: 'new-version-available',
              description: (
                <span>
                  <Button
                    variant="outline"
                    onClick={refreshApp}
                    className="inline h-fit p-1 text-xs"
                  >
                    Refresh now
                  </Button>{' '}
                  or on next page change.
                </span>
              ),
              duration: Infinity,
              position: 'bottom-left',
            })
          }
        }
        return
      }
    }
  }, [
    remoteVersionData,
    refreshApp,
    localAppVersion,
    needsRefresh,
    hasShownToast,
  ])
}
